import type { BadgeTone } from '@shared/ui';

import { OUTCOME_LABEL, reasonCopy } from './outcome-copy';
import { issueLine, normaliseFailure, normaliseIssues } from './report-issues';
import { plural } from './report-values';
import { rowMessage, warningsText } from './row-warnings';
import { recordIssues } from './run-summary';

import type { RowWarning, TenantRow } from '../schemas/bulk-migration';

/**
 * Where one client ended up after a bulk migration, decided from the three rows
 * the job chain holds for it: the analysis (dry-run), the launch, and the
 * newest finished status refresh.
 *
 * The runner's outcomes answer "what did THIS job see"; a category answers
 * "what happened to this client", which is the question an audit is asked.
 * Listed problems first, because that is the order a report is read in.
 */
export const FINAL_CATEGORIES = [
    'failed',
    'error',
    'blocked',
    'refresh_failed',
    'running',
    'pending_refresh',
    'migrated_with_warnings',
    'migrated',
    'not_migratable',
    'not_selected',
    'skipped',
] as const;
export type FinalCategory = (typeof FINAL_CATEGORIES)[number];

export const CATEGORY_LABEL: Readonly<Record<FinalCategory, string>> = {
    failed: 'Failed',
    error: 'Error',
    blocked: 'Blocked',
    refresh_failed: 'Status unreadable',
    running: 'Still running',
    pending_refresh: 'Awaiting refresh',
    migrated_with_warnings: 'Migrated with warnings',
    migrated: 'Migrated',
    not_migratable: 'Not migratable',
    not_selected: 'Not selected',
    skipped: 'Skipped',
};

export const CATEGORY_TONE: Readonly<Record<FinalCategory, BadgeTone>> = {
    failed: 'danger',
    error: 'danger',
    blocked: 'warning',
    refresh_failed: 'warning',
    running: 'info',
    pending_refresh: 'info',
    migrated_with_warnings: 'warning',
    migrated: 'success',
    not_migratable: 'warning',
    not_selected: 'neutral',
    skipped: 'neutral',
};

/**
 * Categories whose client has no settled answer yet: the report is not final
 * while any is non-zero. `refresh_failed` is one: the run exists, only reading
 * it failed, and the next refresh may well read it.
 */
export const UNSETTLED_CATEGORIES: ReadonlySet<FinalCategory> = new Set([
    'refresh_failed',
    'running',
    'pending_refresh',
]);

export interface CategoryVerdict {
    readonly category: FinalCategory;
    /** The machine reason (a runner code), when there is one. */
    readonly reason: string | null;
    /** Why, in words. */
    readonly message: string | null;
}

export interface ClientRows {
    readonly dryRun?: TenantRow | undefined;
    readonly launch?: TenantRow | undefined;
    readonly refresh?: TenantRow | undefined;
}

/**
 * The category of one client.
 *
 * Not launched → the analysis decides (not chosen, not migratable, skipped, or
 * an analysis error). Launched → the refresh row decides when there is one, the
 * launch row otherwise; a launch row alone that started a run is
 * `pending_refresh`, because nothing has read that run yet.
 *
 * The message is the deciding row's own words without its pre-run check
 * findings, then those findings once (`Warnings: …`), whatever the category: a
 * finding is never read as the cause, and never lost. With no words of its own,
 * the category leads (`Blocked. Warnings: …`), so the findings never stand alone
 * where a cause is read.
 */
export function categorise(rows: ClientRows): CategoryVerdict {
    const verdict = decide(rows);
    const findings = warningsText(findingsOf(rows));
    if (findings === null) return verdict;
    const lead = verdict.message ?? `${CATEGORY_LABEL[verdict.category]}.`;
    return { ...verdict, message: `${lead} ${findings}` };
}

/**
 * The pre-run check findings that belong to the client's verdict. Not launched:
 * the analysis'. Launched: the launch's, which a refresh row carries on — except
 * an error row (a refresh that could not read the run), which the runner writes
 * without them; a stale analysis finding is not the launched client's.
 */
function findingsOf(rows: ClientRows): readonly RowWarning[] {
    if (rows.launch === undefined) {
        return rows.dryRun === undefined ? [] : rowMessage(rows.dryRun).warnings;
    }
    const refresh = rows.refresh;
    const source = refresh !== undefined && refresh.outcome !== 'error' ? refresh : rows.launch;
    return rowMessage(source).warnings;
}

/** The row's message without its pre-run check findings: `categorise` appends those. */
function textOf(row: TenantRow): string | null {
    return rowMessage(row).text;
}

function decide(rows: ClientRows): CategoryVerdict {
    const launched = rows.launch;
    if (launched === undefined) return fromAnalysis(rows.dryRun);

    // Launched, but the runner returned no run id (`already-running` with no
    // holder): no refresh can follow it, so waiting for one would never end.
    if (launched.outcome === 'launched' && (launched.run?.runId ?? null) === null) {
        return {
            category: 'error',
            reason: 'run_untracked',
            message:
                'Launched, but the runner returned no run id, so no status refresh can follow this run.',
        };
    }

    // A refresh that failed to READ the run says nothing about the run itself:
    // the runner writes it as an error row without the run, but the launch
    // started one. Not settled, and not "not migrated".
    if (rows.refresh?.outcome === 'error' && launched.run !== undefined) {
        const why = textOf(rows.refresh) ?? reasonCopy(rows.refresh.reason);
        return {
            category: 'refresh_failed',
            reason: rows.refresh.reason,
            message: why
                ? `The run's status could not be read: ${why}`
                : "The run's status could not be read.",
        };
    }

    const row = rows.refresh ?? launched;
    switch (row.outcome) {
        case 'skipped':
            return {
                category: 'skipped',
                reason: row.reason,
                message: reasonCopy(row.reason) ?? textOf(row),
            };
        case 'error':
            return { category: 'error', reason: row.reason, message: textOf(row) };
        case 'blocked':
            return { category: 'blocked', reason: row.reason, message: blockedMessage(row) };
        case 'not_migrated':
            return { category: 'not_migratable', reason: row.reason, message: textOf(row) };
        case 'running':
            return {
                category: 'running',
                reason: row.reason,
                message: 'The run is still in progress.',
            };
        case 'launched':
            // A refresh that answers `launched` found no run header yet: the
            // runner is still preparing it. Without a refresh, nobody has looked.
            return rows.refresh === undefined
                ? {
                      category: 'pending_refresh',
                      reason: row.reason,
                      message:
                          row.reason === 'already_running'
                              ? 'A run was already in progress; no status refresh has read it yet.'
                              : 'Launched; no status refresh has read this run yet.',
                  }
                : {
                      category: 'running',
                      reason: row.reason,
                      message: 'The run is being prepared.',
                  };
        case 'failed':
        case 'completed':
        case 'completed_with_warnings':
            return fromFinishedRun(row);
        default:
            // `pending`, `planned`: a row that never got as far as a run.
            return {
                category: 'pending_refresh',
                reason: row.reason,
                message: `The last report says "${OUTCOME_LABEL[row.outcome]}".`,
            };
    }
}

function fromAnalysis(row: TenantRow | undefined): CategoryVerdict {
    if (row === undefined) {
        return {
            category: 'error',
            reason: 'missing_rows',
            message: 'No job reported this client.',
        };
    }
    switch (row.outcome) {
        case 'planned':
            return {
                category: 'not_selected',
                reason: null,
                message: 'Migratable, but not chosen for this migration.',
            };
        case 'not_migrated':
            return { category: 'not_migratable', reason: row.reason, message: textOf(row) };
        case 'skipped':
            return {
                category: 'skipped',
                reason: row.reason,
                message: reasonCopy(row.reason) ?? textOf(row),
            };
        case 'error':
            return { category: 'error', reason: row.reason, message: textOf(row) };
        default:
            return {
                category: 'error',
                reason: 'analysis_incomplete',
                message: `The analysis ended with "${OUTCOME_LABEL[row.outcome]}" for this client.`,
            };
    }
}

/**
 * A run that ended. One failed record makes the client `failed` even when the
 * run says completed: an audit that rounds a failure up to success is the one
 * report nobody can trust afterwards.
 */
function fromFinishedRun(row: TenantRow): CategoryVerdict {
    const { warnings, failures } = recordIssues(row);
    if (row.outcome === 'failed' || failures > 0) {
        const first = row.records
            .map((record) => normaliseFailure(record.tracking?.failure))
            .find((failure) => failure !== null);
        const aborted = normaliseIssues(row.run?.abortedBy)[0];
        const detail = first?.message ?? aborted?.message ?? textOf(row);
        const lead = failures > 0 ? `${plural(failures, 'record')} failed` : 'The run failed';
        return {
            category: 'failed',
            reason: first?.code ?? aborted?.code ?? row.reason,
            message: detail ? `${lead}: ${detail}` : `${lead}.`,
        };
    }
    if (row.outcome === 'completed_with_warnings' || warnings > 0) {
        const first = row.records.flatMap((record) =>
            normaliseIssues(record.tracking?.warnings),
        )[0];
        const lead = warnings > 0 ? plural(warnings, 'warning') : 'The run reported warnings';
        return {
            category: 'migrated_with_warnings',
            reason: first?.code ?? row.reason,
            message: first ? `${lead}: ${first.message}` : `${lead}.`,
        };
    }
    return { category: 'migrated', reason: null, message: null };
}

/** The findings that stopped the run (`blockedBy`), else the row's own message. */
function blockedMessage(row: TenantRow): string | null {
    const findings = normaliseIssues(row.run?.blockedBy);
    if (findings.length > 0) return findings.map(issueLine).join('; ');
    return textOf(row) ?? reasonCopy(row.reason);
}
